import type { FinanceData } from '../domain/types';
import { createDemoFinanceData } from './demoData';

export interface FinanceLoadResult {
  data: FinanceData;
  mode: 'authenticated' | 'demo';
  integration: IntegrationStatus;
}

export interface AccessContext {
  role: 'owner' | 'qa';
  qaSandbox: boolean;
  canViewNamedPayroll: boolean;
  canMutate: boolean;
}

export interface QuickBooksIntegrationStatus {
  configured: boolean;
  connected: boolean;
  status?: 'connected' | 'disconnected' | 'syncing' | 'error';
  lastSyncedAt?: string | null;
  companyName?: string | null;
  connectUrl?: string | null;
  error?: string | null;
}

export interface IntegrationStatus {
  quickBooks: QuickBooksIntegrationStatus;
  agent: AgentIntegrationStatus;
  workbook: WorkbookIntegrationStatus;
  access?: AccessContext;
  decisionReadiness?: {
    status: 'ready' | 'blocked';
    blockingReasons: string[];
    materialUnmappedAccounts?: AccountMappingException[];
    controls?: {
      accountMapping: {
        status: 'passed' | 'review_required';
        threshold: number;
        approvedMappingCount: number;
        policyVersion?: string;
        appliedPolicyVersion?: string | null;
        materialExceptionCount: number;
        materialExceptionActualTotal: number;
        materialUnmappedAccounts: AccountMappingException[];
        professionalContractorsAllocation?: {
          accountId: string;
          policyVersion: string;
          transactionCount: number;
          transactionCountByMonth: Record<string, number>;
          pendingCount: number;
          monthly: Record<string, { control: number; allocated: number; difference: number }>;
          targetMonthly: Record<string, Record<string, number>>;
          targetTotals: Record<string, number>;
        } | null;
      };
      payroll: {
        status: 'passed' | 'failed' | 'not_applicable';
        tolerance: number;
        testedMonths: number;
        failedMonths: Array<{
          month: string;
          roster: number;
          ledger: number;
          difference: number;
        }>;
        maximumAbsoluteDifference: number;
      };
    };
  };
}

export interface AccountMappingException {
  id: string;
  accountId: string | null;
  name: string;
  side: 'revenue' | 'expense';
  actualTotal: number;
  reviewStatus: 'controller_review' | 'unclassified';
  candidateTargetIds: string[];
  reviewReason: string;
}

export interface AgentIntegrationStatus {
  configured: boolean;
  model: string | null;
  mode: 'ai' | 'calculation_fallback';
}

export interface WorkbookVersion {
  id: string;
  fileName: string;
  fileSize: number;
  activatedAt: string;
  asOfMonth: string;
  summary?: WorkbookPreviewSummary;
}

export interface WorkbookStorageStatus {
  configured: boolean;
  directory: string;
  persistent: boolean;
  writeVerified?: boolean;
  restartRecoveryVerified?: boolean;
  recoveredActiveWorkbook?: boolean;
  recoveredQboSync?: boolean;
  hasManagedState?: boolean;
  evidenceRecordedAt?: string | null;
  priorRecoveryEvidenceAt?: string | null;
  warning?: string | null;
}

export interface RecoveryDrillResult {
  passed: boolean;
  backup: {
    id: string;
    createdAt: string;
    deploymentCommit?: string | null;
    datasetVersion?: string | null;
    files: Array<{ path: string; bytes: number; sha256: string }>;
  };
  datasetVersionBefore: string;
  datasetVersionAfter: string;
  completedAt: string;
}

export interface WorkbookIntegrationStatus {
  active: WorkbookVersion | null;
  versions: WorkbookVersion[];
  storage: WorkbookStorageStatus;
}

export interface WorkbookPreviewSummary {
  previousAsOfMonth: string;
  newAsOfMonth: string;
  importedLineCount: number;
  sourceForecastMonthsVerified: number;
  personnelScheduleChangesIgnored: number;
  annualControls: Record<string, { revenue: number; expenses: number; profit: number }>;
  actualRevenue: number;
  actualRevenueChange: number;
  forecastRevenue: number;
  forecastRevenueChange: number;
  forecastExpenses: number;
  forecastExpensesChange: number;
  warnings: string[];
}

export interface WorkbookPreview {
  token: string;
  expiresAt: string;
  id: string;
  fileName: string;
  fileSize: number;
  summary: WorkbookPreviewSummary;
}

export interface AgentAnswer {
  answer: string;
  mode: 'ai' | 'calculation_fallback';
  model: string | null;
  warning?: string;
  evidence?: AgentEvidence[];
  dataContext?: AgentDataContext;
  quality?: {
    validated: boolean;
    validation: string;
  };
}

export interface AgentEvidence {
  tool?: string;
  label?: string;
  lineId?: string;
  period?: string | null;
  value?: number | null;
  basis?: string | null;
  source?: string | null;
  sourceDetail?: string | null;
  qboAccountIds?: string[];
  qboAccountNames?: string[];
  mappingEvidence?: string | null;
  calculation?: string | null;
  datasetVersion?: string | null;
  syncedAt?: string | null;
  blockingReasons?: string[];
}

export interface AgentDataContext {
  datasetVersion: string | null;
  dataStatus: string | null;
  actualsThrough: string | null;
  latestCompletedMonth: string | null;
  accountingTimeZone: string;
  lastSyncedAt: string | null;
  profitAndLossAsOf: string | null;
  profitAndLossSyncedAt: string | null;
  decisionReady: boolean | null;
  blockingReasons: string[];
}

const demoIntegration: IntegrationStatus = {
  quickBooks: {
    configured: false,
    connected: false,
    status: 'disconnected',
  },
  agent: {
    configured: false,
    model: null,
    mode: 'calculation_fallback',
  },
  workbook: {
    active: null,
    versions: [],
    storage: { configured: false, directory: 'demo', persistent: false },
  },
  access: { role: 'qa', qaSandbox: true, canViewNamedPayroll: false, canMutate: false },
};

const demoEnabled = () =>
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEMO_DATA === 'true';

const isFinanceData = (value: unknown): value is FinanceData => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<FinanceData>;
  return Boolean(
    candidate.company &&
    candidate.asOfMonth &&
    Array.isArray(candidate.months) &&
    Array.isArray(candidate.revenueLines) &&
    Array.isArray(candidate.expenseLines) &&
    Array.isArray(candidate.arAging) &&
    Array.isArray(candidate.debts),
  );
};

export const loadFinanceData = async (): Promise<FinanceLoadResult> => {
  if (window.__DEVERUS_FINANCE_DATA__ && isFinanceData(window.__DEVERUS_FINANCE_DATA__)) {
    return {
      data: window.__DEVERUS_FINANCE_DATA__,
      mode: 'authenticated',
      integration: demoIntegration,
    };
  }

  try {
    const response = await fetch('/api/bootstrap', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Bootstrap request failed (${response.status})`);
    const payload: unknown = await response.json();
    const candidate =
      payload && typeof payload === 'object' && 'data' in payload
        ? (payload as { data: unknown }).data
        : payload;
    if (!isFinanceData(candidate)) throw new Error('Bootstrap returned an invalid finance payload');
    const integration =
      payload && typeof payload === 'object' && 'integration' in payload
        ? (payload as { integration?: IntegrationStatus }).integration
        : undefined;
    const decisionReadiness =
      payload && typeof payload === 'object' && 'metadata' in payload
        ? (payload as { metadata?: { decisionReadiness?: IntegrationStatus['decisionReadiness'] } }).metadata?.decisionReadiness
        : undefined;
    return {
      data: candidate,
      mode: 'authenticated',
      integration: integration?.quickBooks
        ? {
            quickBooks: integration.quickBooks,
            agent: integration.agent ?? demoIntegration.agent,
            workbook: integration.workbook ?? demoIntegration.workbook,
            access: integration.access ?? { role: 'owner', qaSandbox: false, canViewNamedPayroll: true, canMutate: true },
            decisionReadiness,
          }
        : demoIntegration,
    };
  } catch (error) {
    if (!demoEnabled()) throw error;
    return {
      data: createDemoFinanceData(),
      mode: 'demo',
      integration: demoIntegration,
    };
  }
};

export const checkAuthStatus = async (): Promise<boolean> => {
  try {
    const response = await fetch('/api/auth/status', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (response.status === 401) return false;
    if (!response.ok) throw new Error(`Authentication check failed (${response.status})`);
    const payload = await response.json() as { authenticated?: boolean };
    return payload.authenticated === true;
  } catch (error) {
    if (demoEnabled()) return true;
    throw error;
  }
};

export const login = async (password: string): Promise<void> => {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || 'The password was not accepted.');
  }
};

export const logout = async (): Promise<void> => {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Unable to sign out.');
};

export const refreshQuickBooks = async (): Promise<void> => {
  const response = await fetch('/api/qbo/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || 'QuickBooks refresh failed.');
  }
};

const errorFromResponse = async (response: Response, fallback: string) => {
  const payload = await response.json().catch(() => null) as { error?: string } | null;
  if (response.status === 401) window.dispatchEvent(new Event('deverus:session-expired'));
  return new Error(payload?.error || fallback);
};

export const askFinanceAgent = async (
  message: string,
  history: Array<{ role: 'user' | 'assistant'; content: string }>,
): Promise<AgentAnswer> => {
  const response = await fetch('/api/agent/chat', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history }),
  });
  if (!response.ok) throw await errorFromResponse(response, 'The finance agent could not answer that question.');
  return response.json() as Promise<AgentAnswer>;
};

export const previewWorkbookUpload = async (file: File): Promise<WorkbookPreview> => {
  const response = await fetch('/api/data/upload/preview', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'X-File-Name': encodeURIComponent(file.name),
    },
    body: file,
  });
  if (!response.ok) throw await errorFromResponse(response, 'The workbook could not be validated.');
  return response.json() as Promise<WorkbookPreview>;
};

export const activateWorkbookUpload = async (token: string): Promise<void> => {
  const response = await fetch('/api/data/upload/activate', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw await errorFromResponse(response, 'The workbook could not be activated.');
};

export const restoreWorkbookVersion = async (id: string): Promise<void> => {
  const response = await fetch(`/api/data/uploads/${encodeURIComponent(id)}/activate`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw await errorFromResponse(response, 'The prior workbook version could not be restored.');
};

export const runRecoveryDrill = async (): Promise<RecoveryDrillResult> => {
  const response = await fetch('/api/admin/recovery-drill', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw await errorFromResponse(response, 'The backup and restore drill failed.');
  return response.json() as Promise<RecoveryDrillResult>;
};
